// src/pages/TeamInvite.jsx
//
// Landing page for the link emailed to a teacher who was added to a
// learning center's team: /team-invite?token=...
// Public on purpose — the single-use emailed token is the proof.
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SpinnerOverlay from '../components/ui/SpinnerOverlay';

const ENV_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  '';
const API_BASE = ENV_BASE.replace(/\/+$/, '') || 'https://www.doroosonline.co.il';

async function post(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    mode: 'cors',
    credentials: 'omit',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.message || data?.error || 'שגיאת שרת');
  return data;
}

export default function TeamInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // 'ACTIVE' | 'DECLINED'

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setError('הקישור אינו תקין');
      setLoading(false);
      return undefined;
    }
    post('/api/team-invites/preview', { token })
      .then((d) => !cancelled && setInvite(d))
      .catch((e) => !cancelled && setError(e.message || 'ההזמנה אינה תקפה'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [token]);

  const respond = async (action) => {
    setSubmitting(true);
    setError('');
    try {
      const d = await post('/api/team-invites/respond', { token, action });
      setDone(d.status);
    } catch (e) {
      setError(e.message || 'הפעולה נכשלה');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center bg-gray-100 px-4 py-10" dir="rtl">
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-md border border-gray-100 p-6 text-center">
        {(loading || submitting) && <SpinnerOverlay variant="glow" text="נא להמתין" dark={false} blur={false} />}

        {done ? (
          <>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {done === 'ACTIVE' ? 'הצטרפת לצוות!' : 'ההזמנה נדחתה'}
            </h1>
            <p className="text-sm text-gray-600 mb-6">
              {done === 'ACTIVE'
                ? `אתה חלק מצוות המורים של ${invite?.supplierName || 'מרכז הלמידה'}.`
                : 'לא הצטרפת לצוות המורים.'}
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="h-10 px-6 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700"
            >
              לדף הבית
            </button>
          </>
        ) : error && !invite ? (
          <>
            <h1 className="text-xl font-bold text-gray-900 mb-2">ההזמנה אינה זמינה</h1>
            <p className="text-sm text-red-600 mb-6">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="h-10 px-6 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50"
            >
              לדף הבית
            </button>
          </>
        ) : invite ? (
          <>
            <h1 className="text-xl font-bold text-gray-900 mb-2">הזמנה להצטרף לצוות מורים</h1>
            <p className="text-sm text-gray-600 mb-6">
              {invite.teacherName ? `${invite.teacherName}, ` : ''}
              מרכז הלמידה <b>{invite.supplierName}</b> הוסיף אותך לצוות המורים שלו. האם לאשר את ההצטרפות?
            </p>
            {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
            <div className="flex gap-3 justify-center">
              <button
                type="button"
                onClick={() => respond('accept')}
                disabled={submitting}
                className="h-10 px-6 rounded-xl bg-gradient-to-l from-indigo-600 to-violet-600 text-white text-sm font-bold disabled:opacity-50"
              >
                אישור הצטרפות
              </button>
              <button
                type="button"
                onClick={() => respond('decline')}
                disabled={submitting}
                className="h-10 px-6 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                דחייה
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}